import './ClassList.less';

import { DeleteFilled } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDefaultClassCreate,
  useDefaultClassRemove,
  useDefaultClassUpdate,
  useVehicleDefaultClassUpdateByCarrier,
  useVehicleDefaultClassUpdateGlobal,
} from '../../hooks/apiHooks';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import ListHeader from '../common/ListHeader';
import VehicleClassModal from '../modals/VehicleClassModal';

/*
interface IProps {
  id: string;
  carrier?: Carrier;
  vehicles: Vehicle[];
  defaultClasses: DefaultClass[];
}
*/

const RateClassList = ({ carrier, defaultClasses, id, vehicleDefaultClasses, vehicles, }) => {
  const updateMutationByCarrier = useVehicleDefaultClassUpdateByCarrier(carrier?.id || 0);
  const updateMutationGlobal = useVehicleDefaultClassUpdateGlobal();

  const createClassMutation = useDefaultClassCreate();
  const updateClassMutation = useDefaultClassUpdate();
  const removeClassMutation = useDefaultClassRemove();

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);

  const [deletableClass, setDeletableClass] = useState(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [classNames, setClassNames] = useState({});
  const originalFormData = useRef({});
  const originalClassNames = useRef({});

  const initializeClassNames = useCallback(() => {
    const names = {};
    defaultClasses.forEach((dc) => {
      names[dc.id] = dc.name;
    });
    return names;
  }, [defaultClasses]);

  // Convert vehicleDefaultClasses to form data format
  const convertToFormData = useCallback(() => {
    // vehicleDefaultClasses is now already in the correct format: { defaultClassId: [vehicleIds] }
    // We just need to ensure the keys are properly formatted and return a copy
    const formData = {};
    if (vehicleDefaultClasses && typeof vehicleDefaultClasses === 'object') {
      Object.entries(vehicleDefaultClasses).forEach(([defaultClassId, vehicleIds]) => {
        if (Array.isArray(vehicleIds)) {
          formData[defaultClassId] = [...vehicleIds]; // Create a copy of the array
        }
      });
    }
    return formData;
  }, [vehicleDefaultClasses]);

  // Convert form data back to vehicleDefaultClasses format
  const convertFromFormData = useCallback((formData) => {
    const result = [];
    Object.entries(formData).forEach(([defaultClassId, vehicleIds]) => {
      if (Array.isArray(vehicleIds)) {
        vehicleIds.forEach((vehicleId) => {
          result.push({
            carrierId: carrier?.id,
            defaultClassId: parseInt(defaultClassId),
            vehicleId: vehicleId,
          });
        });
      }
    });
    return result;
  }, [carrier?.id]);

  // find classes with changed names
  const changedClassNames = useMemo(() => {
    const changes = [];
    defaultClasses.forEach((dc) => {
      if (classNames[dc.id] && classNames[dc.id] !== dc.name) {
        changes.push({ id: dc.id, name: classNames[dc.id] });
      }
    });
    return changes;
  }, [classNames, defaultClasses]);

  // checking if any class name is empty
  const hasEmptyClassName = useMemo(() => {
    return defaultClasses.some((dc) => !classNames[dc.id]?.trim());
  }, [classNames, defaultClasses]);

  const onSubmit = useCallback(
    async (formData) => {
      // Prevent save if any class name is empty
      if (hasEmptyClassName) {
        return;
      }

      // First, update any changed class names
      for (const changedClass of changedClassNames) {
        await updateClassMutation.mutateAsync(changedClass);
      }

      // then update vehicle default classes
      const vehicleDefaultClassData = convertFromFormData(formData);
      if (carrier) {
        updateMutationByCarrier.mutate(vehicleDefaultClassData);
      } else {
        updateMutationGlobal.mutate(vehicleDefaultClassData);
      }
    },
    [convertFromFormData, updateMutationByCarrier, updateMutationGlobal, carrier, changedClassNames, updateClassMutation, hasEmptyClassName]
  );

  const handleSave = useCallback(() => {
    if (hasEmptyClassName) {
      return;
    }
    form.submit();
  }, [form, hasEmptyClassName]);

  const handleEdit = useCallback(() => {
    // store orig values before editing
    originalFormData.current = form.getFieldsValue();
    originalClassNames.current = { ...classNames };
    setIsEditing(true);
  }, [form, classNames]);

  const handleCancel = useCallback(() => {
    // revert form values
    form.setFieldsValue(originalFormData.current);
    // revert class names
    setClassNames(originalClassNames.current);
    setDeletableClass(undefined);
    setIsEditing(false);
  }, [form]);

  const onAddClass = useCallback(
    (classification) => {
      createClassMutation.mutate(classification);
    },
    [createClassMutation]
  );

  const handleClassNameChange = useCallback((classId, newName) => {
    setClassNames((prev) => ({
      ...prev,
      [classId]: newName,
    }));
  }, []);

  const onDeleteClass = useCallback(() => {
    if (deletableClass) {
      removeClassMutation.mutate(deletableClass);
    }
  }, [deletableClass, removeClassMutation]);

  // Set form values when data loads
  useEffect(() => {
    const formData = convertToFormData();
    form.setFieldsValue(formData);
  }, [convertToFormData, form, vehicleDefaultClasses]);

  // Initialize class names when defaultClasses change
  useEffect(() => {
    setClassNames(initializeClassNames());
  }, [initializeClassNames]);

  // Reset deletable class when editing mode is off
  useEffect(() => {
    if (!isEditing) {
      setDeletableClass(undefined);
    }
  }, [isEditing]);

  // Close add modal on success
  useEffect(() => {
    if (createClassMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createClassMutation.isSuccess]);

  // Turn off editing mode when classes are successfully saved
  useEffect(() => {
    if (updateMutationByCarrier.isSuccess || updateMutationGlobal.isSuccess) {
      setIsEditing(false);
    }
  }, [updateMutationByCarrier.isSuccess, updateMutationGlobal.isSuccess]);

  const isSaving = updateMutationByCarrier.isLoading || updateMutationGlobal.isLoading || updateClassMutation.isLoading;

  return (
    <Container id={id}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Descriptions
          colon={false}
          column={2}
          layout="vertical"
          title={
            <ListHeader>
              <Space>
                <span>API Rate Classes</span>
              </Space>

              <Space>
                {isEditing ? (
                  <>
                    <EditButton
                      disabled={hasEmptyClassName}
                      loading={isSaving}
                      onClick={handleSave}
                    >
                      Save
                    </EditButton>
                    <Button onClick={handleCancel}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <EditButton onClick={handleEdit}>
                    Edit
                  </EditButton>
                )}
                <Button
                  ghost
                  style={{ width: 120 }}
                  type="primary"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Class
                </Button>
              </Space>
            </ListHeader>
          }
        >
          {defaultClasses.map((defaultClass) => (
            <Descriptions.Item
              key={defaultClass.id}
              label={
                <Space>
                  {isEditing ? (
                    <Input
                      placeholder="Enter class name"
                      size="middle"
                      status={!classNames[defaultClass.id]?.trim() ? 'error' : undefined}
                      style={{ width: 155 }}
                      value={classNames[defaultClass.id] || ''}
                      onChange={(e) => handleClassNameChange(defaultClass.id, e.target.value)}
                    />
                  ) : (
                    <span>{defaultClass.name}</span>
                  )}
                  {isEditing && (
                    <Popconfirm
                      okButtonProps={{ loading: removeClassMutation.isLoading }}
                      open={deletableClass?.id === defaultClass.id}
                      title="Are you sure to delete this class?"
                      onCancel={() => setDeletableClass(undefined)}
                      onConfirm={onDeleteClass}
                    >
                      <DeleteFilled
                        style={{ color: '#ff4d4f', cursor: 'pointer' }}
                        onClick={() => setDeletableClass(defaultClass)}
                      />
                    </Popconfirm>
                  )}
                </Space>
              }
            >
              <Form.Item
                key={defaultClass.id}
                noStyle
                name={defaultClass.id}
              >
                <Select
                  className="class-list__vehicle-select"
                  disabled={!isEditing}
                  mode="multiple"
                  placeholder="Select vehicle types"
                  style={{ width: '100%' }}
                >
                  {vehicles.map((vehicle) => (
                    <Select.Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Descriptions.Item>
          ))}
          {defaultClasses.length % 2 === 1 && (
            <Descriptions.Item>&nbsp;</Descriptions.Item>
          )}
        </Descriptions>
      </Form>

      {showAddModal && (
        <VehicleClassModal
          error={createClassMutation.error}
          submitting={createClassMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAddClass}
        />
      )}
    </Container>
  );
};

export default RateClassList;
